import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Send, Building, MessageCircle, CreditCard, Bike, Copy, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function AdminSettings() {
  const { user } = useAuth();
  const expired = user?.subscription_active === false;
  const [form, setForm] = useState({ name: "", address: "", phone: "" });
  const [loading, setLoading] = useState(true);

  // Notify owner state (works even when expired — user needs to reach out)
  const [msg, setMsg] = useState({ subject: "", message: "" });
  const [sending, setSending] = useState(false);

  // Delivery + Payment settings
  const [delivery, setDelivery] = useState({ slug: "", delivery_enabled: false, public_url: "" });
  const [payment, setPayment] = useState({ payment_link: "", payment_instructions: "" });
  const [savingPayment, setSavingPayment] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/restaurant");
        setForm({ name: data.name || "", address: data.address || "", phone: data.phone || "" });
        const [{ data: d }, { data: p }] = await Promise.all([
          api.get("/admin/settings/delivery"),
          api.get("/admin/settings/payment"),
        ]);
        setDelivery(d);
        setPayment(p);
      } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
      finally { setLoading(false); }
    })();
  }, []);

  const toggleDelivery = async (enabled) => {
    try {
      await api.put("/admin/settings/delivery", { delivery_enabled: enabled });
      setDelivery((d) => ({ ...d, delivery_enabled: enabled }));
      toast.success(enabled ? "Delivery activado — comparte tu link" : "Delivery desactivado");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const savePayment = async (e) => {
    e.preventDefault();
    setSavingPayment(true);
    try {
      await api.put("/admin/settings/payment", payment);
      toast.success("Link de cobro guardado");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSavingPayment(false); }
  };

  const copyPublicUrl = async () => {
    const url = `${window.location.origin}/r/${delivery.slug}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copiado");
    } catch {
      toast.error("Copia manualmente: " + url);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put("/admin/restaurant", form);
      toast.success("Información actualizada");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!msg.subject.trim() || !msg.message.trim()) {
      toast.error("Asunto y mensaje son obligatorios");
      return;
    }
    setSending(true);
    try {
      await api.post("/admin/notify-owner", msg);
      toast.success("Mensaje enviado al owner");
      setMsg({ subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSending(false); }
  };

  if (loading) return <p className="text-[#7248ae]">Cargando…</p>;

  return (
    <div className="animate-fade-up max-w-2xl space-y-8" data-testid="admin-settings-page">
      <div>
        <p className="text-xs uppercase tracking-[0.3em] text-[#7248ae] mb-2">Configuración</p>
        <h1 className="font-display text-4xl sm:text-5xl font-black text-[#1a0f2e] tracking-tight">Ajustes</h1>
      </div>

      <Card className="p-6 border border-[#b182ec]/30 rounded-2xl">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-[#3e166c]/10 flex items-center justify-center">
            <Building className="h-5 w-5 text-[#3e166c]" />
          </div>
          <div>
            <h2 className="font-display text-xl font-bold text-[#1a0f2e]">Restaurante</h2>
            <p className="text-sm text-[#7248ae]">Información que aparecerá en los tickets.</p>
          </div>
        </div>
        <form onSubmit={save} className="space-y-5" data-testid="restaurant-form">
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#7248ae] mb-2 block">Nombre del restaurante</Label>
            <Input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} disabled={expired} data-testid="restaurant-name-input" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#7248ae] mb-2 block">Dirección</Label>
            <Textarea rows={3} value={form.address} onChange={e=>setForm({...form,address:e.target.value})} disabled={expired} data-testid="restaurant-address-input" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#7248ae] mb-2 block">Teléfono</Label>
            <Input value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})} disabled={expired} data-testid="restaurant-phone-input" />
          </div>
          <Button type="submit" disabled={expired} className="bg-[#3e166c] hover:bg-[#7248ae] text-white" data-testid="save-restaurant-button">Guardar cambios</Button>
        </form>
      </Card>

      <Card className="p-6 border border-[#b182ec]/30 rounded-2xl" data-testid="delivery-settings-card">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-[#3e166c]/10 flex items-center justify-center">
            <Bike className="h-5 w-5 text-[#3e166c]" />
          </div>
          <div className="flex-1">
            <h2 className="font-display text-xl font-bold text-[#1a0f2e]">Cobro & Delivery</h2>
            <p className="text-sm text-[#7248ae]">Habilita pedidos a domicilio y configura tu link de cobro.</p>
          </div>
        </div>

        <div className="flex items-center justify-between p-3 rounded-xl bg-[#fcfbfd] border border-[#b182ec]/30 mb-5">
          <div>
            <p className="text-sm font-semibold text-[#1a0f2e]">Aceptar pedidos por delivery</p>
            <p className="text-xs text-[#7248ae]">Tus clientes podrán pedir desde {window.location.origin}/r/{delivery.slug || "…"}</p>
          </div>
          <Switch
            checked={delivery.delivery_enabled}
            onCheckedChange={toggleDelivery}
            disabled={expired}
            data-testid="delivery-toggle"
          />
        </div>

        {delivery.delivery_enabled && (
          <div className="flex flex-wrap gap-2 mb-6 p-3 rounded-xl bg-[#3e166c]/5 border border-[#3e166c]/20" data-testid="delivery-public-url">
            <code className="flex-1 min-w-0 text-xs text-[#3e166c] break-all">{window.location.origin}/r/{delivery.slug}</code>
            <Button type="button" size="sm" variant="outline" onClick={copyPublicUrl} className="border-[#b182ec]/50">
              <Copy className="h-3 w-3 mr-1" />Copiar
            </Button>
            <Button type="button" size="sm" variant="outline" asChild className="border-[#b182ec]/50">
              <a href={`/r/${delivery.slug}`} target="_blank" rel="noreferrer">
                <ExternalLink className="h-3 w-3 mr-1" />Abrir
              </a>
            </Button>
          </div>
        )}

        <form onSubmit={savePayment} className="space-y-4" data-testid="payment-link-form">
          <div className="flex items-center gap-2 text-[#1a0f2e]">
            <CreditCard className="h-4 w-4 text-[#7248ae]" />
            <p className="font-semibold">Link de cobro</p>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#7248ae] mb-2 block">URL del link de pago (Stripe, Mercado Pago, transferencia, etc.)</Label>
            <Input
              value={payment.payment_link}
              onChange={(e) => setPayment({ ...payment, payment_link: e.target.value })}
              placeholder="https://buy.stripe.com/..."
              disabled={expired}
              data-testid="payment-link-input"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#7248ae] mb-2 block">Instrucciones (opcional)</Label>
            <Textarea
              rows={3}
              value={payment.payment_instructions}
              onChange={(e) => setPayment({ ...payment, payment_instructions: e.target.value })}
              placeholder="Ej: Acepta tarjeta o transferencia. Datos: cuenta 1234..."
              disabled={expired}
              data-testid="payment-instructions-input"
            />
          </div>
          <Button type="submit" disabled={expired || savingPayment} className="bg-[#3e166c] hover:bg-[#7248ae] text-white" data-testid="save-payment-button">
            {savingPayment ? "Guardando…" : "Guardar link de cobro"}
          </Button>
        </form>
      </Card>

      <Card className="p-6 border border-[#b182ec]/30 rounded-2xl">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-[#3e166c]/10 flex items-center justify-center">
            <MessageCircle className="h-5 w-5 text-[#3e166c]" />
          </div>
          <div>
            <h2 className="font-display text-xl font-bold text-[#1a0f2e]">Contactar al Owner</h2>
            <p className="text-sm text-[#7248ae]">Envíale un mensaje directo. Recibirás respuesta por correo.</p>
          </div>
        </div>
        <form onSubmit={sendMessage} className="space-y-4 mt-5" data-testid="notify-owner-form">
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#7248ae] mb-2 block">Asunto</Label>
            <Input
              value={msg.subject}
              onChange={e => setMsg({ ...msg, subject: e.target.value })}
              placeholder="Solicitud de renovación, consulta…"
              maxLength={120}
              data-testid="notify-subject-input"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#7248ae] mb-2 block">Mensaje</Label>
            <Textarea
              rows={5}
              value={msg.message}
              onChange={e => setMsg({ ...msg, message: e.target.value })}
              placeholder="Escribe aquí tu mensaje…"
              maxLength={2000}
              data-testid="notify-message-input"
            />
            <p className="text-xs text-[#7248ae] mt-1">{msg.message.length} / 2000</p>
          </div>
          <Button type="submit" disabled={sending} className="bg-[#3e166c] hover:bg-[#7248ae] text-white" data-testid="notify-send-button">
            <Send className="h-4 w-4 mr-2" />{sending ? "Enviando…" : "Enviar al owner"}
          </Button>
        </form>
      </Card>
    </div>
  );
}
